import { useId, useState, type ReactNode } from 'react';
import Decimal from 'decimal.js';
import { X } from 'lucide-react';
import { percentualParaTaxa, type ApropriacaoEntrada, type ApropriacaoResponse } from '@triade/contracts';
import { usePreviaApropriacao, type ReferTo } from '@/api/apropriacoes';
import { useAnaliticos } from '@/api/centros-custo';
import { useCriterios } from '@/api/criterios-rateio';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ehDecimal, formatarFracaoPercentual, formatarMoeda, fracaoParaPercentualTexto } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Donut, type FatiaDonut } from './donut';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';

export type { ReferTo } from '@/api/apropriacoes';
type Tipo = ApropriacaoEntrada['tipo'];

export interface ErroApropriacao {
  code: string;
  message: string;
}

/** completa = pode ser gravada. MANUAL: linhas completas, sem centro repetido e soma exatamente 100%. IGUAL: ao menos 1 centro. CRITERIO: criterio escolhido. */
export interface SituacaoApropriacao {
  completa: boolean;
}

export interface ApropriacaoPorCentroProps {
  /** Valor pronto para a API (fracoes em string). null = sem apropriacao ainda (ou incompleta). */
  value?: ApropriacaoEntrada | null;
  /** Recebe a entrada montada (null se incompleta) e a situacao. Chamado so em alteracao do usuario, nunca na abertura. */
  onChange?: (v: ApropriacaoEntrada | null, situacao?: SituacaoApropriacao) => void;
  /** Leitura da API (apos gravar). No modo 'leitura' e a unica fonte. Na edicao, IGUAL e CRITERIO vem da previa, nao daqui. */
  resolvida?: ApropriacaoResponse | null;
  referTo: ReferTo;
  disabled?: boolean;
  /** Erro devolvido pela API ao gravar (ex.: SOMA_DIFERENTE_DE_100). */
  erro?: ErroApropriacao | null;
  /** 'edicao' (padrao) ou 'leitura' (so mostra `resolvida`, sem chamar a previa). */
  modo?: 'edicao' | 'leitura';
  /** Ids de centros que podem ser escolhidos. Quando vier, centros fora da lista nao aparecem (selects, checkboxes e rotulos). */
  centrosPermitidos?: string[];
  /** Valor a ratear (string decimal). Quando vier, cada linha mostra R$ = valorBase x % e um Total. So exibicao. */
  valorBase?: string;
}

interface LinhaRascunho {
  chave: number;
  centroId: string;
  /** Texto digitado ("50", "33,5"). Convertido com percentualParaTaxa ao chamar onChange. */
  texto: string;
}

interface Rascunho {
  tipo: Tipo;
  linhas: LinhaRascunho[];
  iguais: string[];
  criterioId: string | null;
}

const ROTULO_REFERE: Record<ReferTo, string> = { PESSOA: 'pessoa', IMOBILIZADO: 'imobilizado', CIF: 'CIF', CENTRO_CUSTO: 'centro de apoio' };
const ROTULO_TIPO: Record<Tipo, string> = { MANUAL: 'Manual', IGUAL: 'Igual', CRITERIO: 'Critério' };
const TIPOS: Tipo[] = ['MANUAL', 'IGUAL', 'CRITERIO'];

/** valorBase so vale se for decimal valido; senao a coluna R$ nao aparece. */
function valorBaseValido(valorBase: string | undefined): string | null {
  return valorBase !== undefined && ehDecimal(valorBase) ? valorBase : null;
}

/** Exibicao: valorBase x fracao (Decimal). Fracao ausente = null (mostra '—'). */
function valorDaLinha(base: string | null, fracao: string | null | undefined): string | null {
  if (base === null || fracao === null || fracao === undefined) return null;
  return new Decimal(base).times(fracao).toString();
}

function totalDosValores(valores: (string | null)[]): string {
  return valores.reduce<Decimal>((acc, v) => (v === null ? acc : acc.plus(v)), new Decimal(0)).toString();
}

let seqLinha = 0;
function novaLinha(centroId = '', texto = ''): LinhaRascunho {
  seqLinha += 1;
  return { chave: seqLinha, centroId, texto };
}

/** Linha completa: centro escolhido e percentual digitado valido (0 a 100). */
function linhaCompleta(l: LinhaRascunho): boolean {
  return l.centroId !== '' && percentualParaTaxa(l.texto) !== null;
}

/** Monta o ValueObject da API a partir do rascunho. Incompleto ou repetido vira null (a API nao recebe nada parcial). */
function montar(r: Rascunho): ApropriacaoEntrada | null {
  if (r.tipo === 'IGUAL') return r.iguais.length > 0 ? { tipo: 'IGUAL', centroIds: r.iguais } : null;
  if (r.tipo === 'CRITERIO') return r.criterioId ? { tipo: 'CRITERIO', criterioId: r.criterioId } : null;
  if (r.linhas.length === 0) return null;
  const linhas: { centroId: string; percentual: string }[] = [];
  const vistos = new Set<string>();
  for (const l of r.linhas) {
    const percentual = percentualParaTaxa(l.texto);
    if (!l.centroId || percentual === null || vistos.has(l.centroId)) return null;
    vistos.add(l.centroId);
    linhas.push({ centroId: l.centroId, percentual });
  }
  return { tipo: 'MANUAL', linhas };
}

function iniciar(value: ApropriacaoEntrada | null): Rascunho {
  const base: Rascunho = { tipo: 'MANUAL', linhas: [], iguais: [], criterioId: null };
  if (!value) return base;
  switch (value.tipo) {
    case 'MANUAL':
      return {
        ...base,
        linhas: value.linhas.map((l) => novaLinha(l.centroId, fracaoParaPercentualTexto(l.percentual))),
      };
    case 'IGUAL':
      return { ...base, tipo: 'IGUAL', iguais: value.centroIds };
    case 'CRITERIO':
      return { ...base, tipo: 'CRITERIO', criterioId: value.criterioId };
  }
}

/**
 * Soma das fracoes digitadas validas, SO para o aviso e a situacao. A API valida de verdade (SOMA_DIFERENTE_DE_100).
 * Decimal.js porque a soma nao pode passar por number.
 */
function somaManual(linhas: LinhaRascunho[]): Decimal {
  return linhas.reduce((acc, l) => {
    const p = percentualParaTaxa(l.texto);
    return p === null ? acc : acc.plus(p);
  }, new Decimal(0));
}

/** Situacao do rascunho: so define se pode ser gravado. Nenhum percentual de IGUAL/CRITERIO sai daqui (vem da previa). */
function situacaoDe(r: Rascunho): SituacaoApropriacao {
  if (r.tipo === 'IGUAL') return { completa: r.iguais.length > 0 };
  if (r.tipo === 'CRITERIO') return { completa: r.criterioId !== null };
  const ids = new Set(r.linhas.map((l) => l.centroId));
  const completa = r.linhas.length > 0 && r.linhas.every(linhaCompleta) && ids.size === r.linhas.length && somaManual(r.linhas).equals(1);
  return { completa };
}

/** Grade e donut lado a lado (lg); empilhados em telas menores. */
function DuasColunas({ grade, fatias }: { grade: ReactNode; fatias: FatiaDonut[] }) {
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]">
      <div className="min-w-0 space-y-2">{grade}</div>
      <div className="min-w-0 rounded-xl border border-border bg-card p-3">
        <Donut fatias={fatias} anelVazio />
      </div>
    </div>
  );
}

/** Modo leitura: so o que a API devolveu em `resolvida`. Sem chamadas e sem controles. */
export function ApropriacaoLeitura({ apropriacao }: { apropriacao: ApropriacaoResponse | null }) {
  if (!apropriacao) return <p className="text-small text-ink-muted">Sem apropriação por centro.</p>;
  const criterio = apropriacao.tipo === 'CRITERIO' && apropriacao.criterioNome ? ` (${apropriacao.criterioNome})` : '';
  const linhas = apropriacao.erro ? [] : apropriacao.linhas;
  const grade = (
    <>
      {apropriacao.erro && (
        <div role="alert" className="rounded-xl bg-warning-bg px-3 py-2 text-small text-warning-ink">
          {apropriacao.erro.message}
        </div>
      )}
      <Table aria-label="Proporções por centro">
        <TableHeader>
          <TableRow>
            <TableHead>Centro</TableHead>
            <TableHead className="text-right">%</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.map((l) => (
            <TableRow key={l.centroId}>
              <TableCell>{`${l.caminho} — ${l.nome}`}</TableCell>
              <TableCell className="tabular text-right">{formatarFracaoPercentual(l.percentual)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
  return (
    <div className="space-y-3">
      <p className="text-small font-medium text-ink-secondary">{`Tipo: ${ROTULO_TIPO[apropriacao.tipo]}${criterio}`}</p>
      <DuasColunas
        grade={grade}
        fatias={linhas.map((l) => ({ rotulo: `${l.caminho} — ${l.nome}`, fracao: l.percentual }))}
      />
    </div>
  );
}

/**
 * Apropriacao por centro de custo (Pessoas, Imobilizados, depois CIF). Tres tipos: MANUAL (percentuais digitados), IGUAL
 * (o usuario escolhe os centros; a API divide) e CRITERIO (a API calcula pela base do criterio). Nao calcula custo nem
 * percentual: os percentuais de IGUAL e CRITERIO sao sempre da previa (POST /apropriacoes/previa).
 * Dica de uso: a tela deve montar o componente depois que o cadastro carregou (rascunho e iniciado a partir de `value`).
 */
export function ApropriacaoPorCentro({ modo = 'edicao', ...resto }: ApropriacaoPorCentroProps) {
  if (modo === 'leitura') return <ApropriacaoLeitura apropriacao={resto.resolvida ?? null} />;
  return <EdicaoApropriacao {...resto} />;
}

type EdicaoProps = Omit<ApropriacaoPorCentroProps, 'modo' | 'resolvida'>;

function EdicaoApropriacao({ value = null, onChange, referTo, disabled = false, erro = null, centrosPermitidos, valorBase }: EdicaoProps) {
  const grupo = useId();
  const centrosQuery = useAnaliticos();
  const criteriosQuery = useCriterios();
  const [rascunho, setRascunho] = useState<Rascunho>(() => iniciar(value));
  const base = valorBaseValido(valorBase);

  const todosCentros = centrosQuery.data ?? [];
  const centros = centrosPermitidos ? todosCentros.filter((c) => centrosPermitidos.includes(c.id)) : todosCentros;
  const criterios = (criteriosQuery.data ?? []).filter((c) => c.ativo);
  const rotuloDoCentro = new Map(centros.map((c) => [c.id, `${c.caminho} — ${c.nome}`]));

  /** Previa so para IGUAL e CRITERIO (MANUAL e digitado). Sem entrada completa, nao consulta. */
  const entradaPrevia = rascunho.tipo === 'MANUAL' ? null : montar(rascunho);
  const previa = usePreviaApropriacao(referTo, entradaPrevia);
  /** Enquanto a previa nova nao chega (placeholder), nao mostra percentuais de outra escolha. */
  const previaAtual = previa.isPlaceholderData || previa.data?.tipo !== rascunho.tipo || rascunho.tipo === 'MANUAL' ? null : previa.data;
  const previaErro = previaAtual?.erro ?? null;

  function atualizar(proximo: Rascunho) {
    setRascunho(proximo);
    onChange?.(montar(proximo), situacaoDe(proximo));
  }

  function trocarTipo(tipo: Tipo) {
    const linhas = tipo === 'MANUAL' && rascunho.linhas.length === 0 ? [novaLinha()] : rascunho.linhas;
    atualizar({ ...rascunho, tipo, linhas });
  }

  function alterarLinha(chave: number, parcial: Partial<Pick<LinhaRascunho, 'centroId' | 'texto'>>) {
    atualizar({ ...rascunho, linhas: rascunho.linhas.map((l) => (l.chave === chave ? { ...l, ...parcial } : l)) });
  }

  const soma = somaManual(rascunho.linhas);
  const somaCemPorCento = soma.equals(1);
  const incompletas = rascunho.linhas.filter((l) => !linhaCompleta(l)).length;

  const fatias: FatiaDonut[] =
    rascunho.tipo === 'MANUAL'
      ? rascunho.linhas.filter(linhaCompleta).map((l) => ({
          rotulo: rotuloDoCentro.get(l.centroId) ?? '',
          fracao: percentualParaTaxa(l.texto) ?? '0',
        }))
      : previaAtual && !previaAtual.erro
        ? previaAtual.linhas.map((l) => ({ rotulo: `${l.caminho} — ${l.nome}`, fracao: l.percentual }))
        : [];

  const alertaPrevia = previa.isError ? (
    <div role="alert" className="rounded-xl bg-danger-bg px-3 py-2 text-small text-danger-ink">
      Não foi possível calcular os percentuais: {previa.error.message}
    </div>
  ) : null;

  let grade: ReactNode;
  if (rascunho.tipo === 'MANUAL') {
    grade = (
      <>
        <Table aria-label="Proporções por centro">
          <TableHeader>
            <TableRow>
              <TableHead>Centro</TableHead>
              <TableHead className="w-36 text-right">%</TableHead>
              {base && <TableHead className="w-32 text-right">R$</TableHead>}
              <TableHead className="w-12">
                <span className="sr-only">Ações</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rascunho.linhas.length === 0 && (
              <TableRow>
                <TableCell colSpan={base ? 4 : 3} className="text-ink-muted">
                  Nenhum centro. Use “+ centro”.
                </TableCell>
              </TableRow>
            )}
            {rascunho.linhas.map((l, i) => {
              const textoInvalido = l.texto.trim() !== '' && percentualParaTaxa(l.texto) === null;
              const usados = new Set(rascunho.linhas.filter((x) => x.chave !== l.chave).map((x) => x.centroId));
              return (
                <TableRow key={l.chave}>
                  <TableCell>
                    <select
                      aria-label={`Centro da linha ${i + 1}`}
                      value={l.centroId}
                      onChange={(e) => alterarLinha(l.chave, { centroId: e.target.value })}
                      className="h-10 w-full min-w-0 rounded-[10px] border border-input bg-card px-3 text-small"
                    >
                      <option value="">Escolha o centro…</option>
                      {centros
                        .filter((c) => !usados.has(c.id))
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {`${c.caminho} — ${c.nome}`}
                          </option>
                        ))}
                    </select>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center justify-end gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`Percentual da linha ${i + 1}`}
                        aria-invalid={textoInvalido}
                        placeholder="0"
                        value={l.texto}
                        onChange={(e) => alterarLinha(l.chave, { texto: e.target.value })}
                        className={cn(
                          'tabular h-10 w-24 rounded-[10px] border bg-card px-3 text-right',
                          textoInvalido ? 'border-danger-ink' : 'border-input',
                        )}
                      />
                      <span className="text-small text-ink-muted">%</span>
                    </span>
                  </TableCell>
                  {base && (
                    <TableCell className="tabular text-right">{formatarMoeda(valorDaLinha(base, percentualParaTaxa(l.texto)))}</TableCell>
                  )}
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remover linha ${i + 1}`}
                      onClick={() => atualizar({ ...rascunho, linhas: rascunho.linhas.filter((x) => x.chave !== l.chave) })}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {base && rascunho.linhas.length > 0 && (
              <TableRow>
                <TableCell className="font-medium">Total</TableCell>
                <TableCell />
                <TableCell className="tabular text-right font-medium">
                  {formatarMoeda(totalDosValores(rascunho.linhas.map((l) => valorDaLinha(base, percentualParaTaxa(l.texto)))))}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Button type="button" variant="outline" size="sm" onClick={() => atualizar({ ...rascunho, linhas: [...rascunho.linhas, novaLinha()] })}>
          + centro
        </Button>

        <p className={cn('tabular text-small', somaCemPorCento ? 'text-ink-secondary' : 'text-warning-ink')}>
          {`Soma: ${formatarFracaoPercentual(soma.toString())}${somaCemPorCento ? '' : ' — precisa ser 100%'}`}
        </p>
        {incompletas > 0 && (
          <p role="status" className="text-small text-ink-muted">
            {incompletas === 1 ? '1 linha incompleta' : `${incompletas} linhas incompletas`}: escolha o centro e um percentual de 0 a 100. A apropriação só é enviada completa.
          </p>
        )}
      </>
    );
  } else if (rascunho.tipo === 'IGUAL') {
    grade = (
      <>
        {alertaPrevia}
        {previa.isFetching && <p role="status" className="text-small text-ink-muted">Calculando percentuais…</p>}
        <Table aria-label="Proporções por centro">
          <TableHeader>
            <TableRow>
              <TableHead>Centro</TableHead>
              <TableHead className="w-36 text-right">%</TableHead>
              {base && <TableHead className="w-32 text-right">R$</TableHead>}
              <TableHead className="w-12">
                <span className="sr-only">Ações</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rascunho.iguais.length === 0 && (
              <TableRow>
                <TableCell colSpan={base ? 4 : 3} className="text-ink-muted">
                  Escolha ao menos um centro.
                </TableCell>
              </TableRow>
            )}
            {rascunho.iguais.map((id) => {
              const linha = previaAtual?.linhas.find((x) => x.centroId === id);
              const nome = linha ? `${linha.caminho} — ${linha.nome}` : (rotuloDoCentro.get(id) ?? '…');
              return (
                <TableRow key={id}>
                  <TableCell className="min-w-0 truncate">{nome}</TableCell>
                  <TableCell className="tabular text-right">{linha ? formatarFracaoPercentual(linha.percentual) : '—'}</TableCell>
                  {base && <TableCell className="tabular text-right">{formatarMoeda(valorDaLinha(base, linha?.percentual))}</TableCell>}
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remover ${nome}`}
                      onClick={() => atualizar({ ...rascunho, iguais: rascunho.iguais.filter((x) => x !== id) })}
                    >
                      <X aria-hidden="true" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {base && rascunho.iguais.length > 0 && (
              <TableRow>
                <TableCell className="font-medium">Total</TableCell>
                <TableCell />
                <TableCell className="tabular text-right font-medium">
                  {formatarMoeda(
                    totalDosValores(rascunho.iguais.map((id) => valorDaLinha(base, previaAtual?.linhas.find((x) => x.centroId === id)?.percentual))),
                  )}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
        {centros.length === 0 && centrosQuery.isSuccess && <p className="text-small text-ink-muted">Nenhum centro analítico cadastrado.</p>}
        <select
          aria-label="Adicionar centro"
          value=""
          onChange={(e) => {
            if (e.target.value) atualizar({ ...rascunho, iguais: [...rascunho.iguais, e.target.value] });
          }}
          className="h-10 w-full min-w-0 rounded-[10px] border border-input bg-card px-3 text-small"
        >
          <option value="">Adicionar centro…</option>
          {centros
            .filter((c) => !rascunho.iguais.includes(c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {`${c.caminho} — ${c.nome}`}
              </option>
            ))}
        </select>
      </>
    );
  } else {
    grade = (
      <>
        {alertaPrevia}
        {previa.isFetching && <p role="status" className="text-small text-ink-muted">Calculando percentuais…</p>}
        {rascunho.criterioId === null && <p className="text-small text-ink-muted">Escolha o critério para ver os percentuais.</p>}
        {previaErro && (
          <div role="alert" className="rounded-xl bg-warning-bg px-3 py-2 text-small text-warning-ink">
            {previaErro.message}
          </div>
        )}
        {previaAtual && !previaErro && (
          <Table aria-label="Proporções por centro">
            <TableHeader>
              <TableRow>
                <TableHead>Centro</TableHead>
                <TableHead className="text-right">%</TableHead>
                {base && <TableHead className="w-32 text-right">R$</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {previaAtual.linhas.map((l) => (
                <TableRow key={l.centroId}>
                  <TableCell>{`${l.caminho} — ${l.nome}`}</TableCell>
                  <TableCell className="tabular text-right">{formatarFracaoPercentual(l.percentual)}</TableCell>
                  {base && <TableCell className="tabular text-right">{formatarMoeda(valorDaLinha(base, l.percentual))}</TableCell>}
                </TableRow>
              ))}
              {base && previaAtual.linhas.length > 0 && (
                <TableRow>
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell />
                  <TableCell className="tabular text-right font-medium">
                    {formatarMoeda(totalDosValores(previaAtual.linhas.map((l) => valorDaLinha(base, l.percentual))))}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </>
    );
  }

  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-3">
      <legend className="mb-1 text-caption text-ink-muted">Apropriação por centro de custo</legend>

      {erro && (
        <div role="alert" className="rounded-xl bg-danger-bg px-3 py-2 text-small text-danger-ink">
          {erro.message} <span className="tabular">({erro.code})</span>
        </div>
      )}

      <section aria-label="Configuração da apropriação" className="space-y-2">
        <p className="text-caption font-medium text-ink-secondary">Configuração</p>
        <div className="flex flex-wrap items-center gap-3">
          <div
            role="radiogroup"
            aria-label={`Tipo de apropriação do ${ROTULO_REFERE[referTo]}`}
            className="inline-flex rounded-[10px] border border-input bg-card p-0.5"
          >
            {TIPOS.map((t) => (
              <label
                key={t}
                className={cn(
                  'cursor-pointer rounded-md px-3 py-1 text-small',
                  rascunho.tipo === t ? 'bg-primary text-primary-foreground' : 'text-ink-body hover:bg-line-soft',
                  disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                <input
                  type="radio"
                  name={grupo}
                  value={t}
                  checked={rascunho.tipo === t}
                  onChange={() => trocarTipo(t)}
                  className="sr-only"
                />
                {ROTULO_TIPO[t]}
              </label>
            ))}
          </div>

          {rascunho.tipo === 'CRITERIO' && (
            <div className="min-w-55 flex-1">
              <select
                aria-label="Critério de rateio"
                value={rascunho.criterioId ?? ''}
                onChange={(e) => atualizar({ ...rascunho, criterioId: e.target.value || null })}
                className="h-10 w-full rounded-[10px] border border-input bg-card px-3 text-small"
              >
                <option value="">Escolha o critério…</option>
                {criterios.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.identificador} — ${c.descricao}`}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        {rascunho.tipo === 'CRITERIO' && criteriosQuery.isSuccess && criterios.length === 0 && <p className="text-small text-ink-muted">Nenhum critério ativo cadastrado.</p>}
        {rascunho.tipo === 'CRITERIO' && criteriosQuery.isError && (
          <ErroFormacao erro={criteriosQuery.error} onTentar={() => void criteriosQuery.refetch()} titulo="Não foi possível carregar os critérios" />
        )}
      </section>

      {centrosQuery.isPending && <p role="status" className="text-small text-ink-muted">Carregando centros…</p>}
      {centrosQuery.isError && (
        <ErroFormacao erro={centrosQuery.error} onTentar={() => void centrosQuery.refetch()} titulo="Não foi possível carregar os centros" />
      )}

      <DuasColunas grade={grade} fatias={fatias} />
    </fieldset>
  );
}

